/**
 * Military Missions Configuration
 * 
 * Defines mission types and their effects on casualties and resource allocation.
 * Used for nested tactical positioning at Division/Brigade/Battalion levels.
 * 
 * Mission affects casualty rates based on unit's role in the operation:
 * - MAIN_EFFORT: Primary attacking force, highest casualties
 * - SUPPORTING: Supports main effort, moderate casualties
 * - RESERVE: Out of immediate contact, minimal casualties
 * - EXPLOITATION: Exploits success, high mobility, moderate-high casualties
 * 
 * @author Dr. Tatar Radu MD, Romanian Military Medical Institute
 * @date November 27, 2025
 */

import { ICONS } from '../utils/icons';

// ============================================================================
// MISSION TYPES
// ============================================================================

export const MISSIONS = {
  MAIN_EFFORT: {
    value: 'MAIN_EFFORT',
    label: 'Main Effort',
    icon: ICONS.TACTICAL.MAIN_EFFORT, // ⚔️
    
    // Casualty multiplier
    casualty_multiplier: 1.5,
    
    // Description
    description: 'Primary attacking force - receives priority of fires and resources',
    
    // Tactical implications
    priority: 'HIGHEST',
    resources_priority: 'FIRST',
    commander_focus: 'PRIMARY',
    
    // Medical implications
    medical_priority: 'HIGHEST',
    expected_casualties: 'HIGH',
    evacuation_priority: 'URGENT',
    
    // Examples
    examples: [
      'Brigade designated to breach enemy defenses',
      'Division making primary attack in corps operation',
      'Battalion leading river crossing'
    ],
    
    // Romanian context
    romanian_term: 'Efortul Principal',
    
    // Teaching points
    teaching_points: [
      'Receives majority of combat power',
      'Commander\'s focus and priority',
      'Highest casualty rates expected',
      'Medical assets positioned to support',
      'Evacuation routes prioritized'
    ]
  },

  SUPPORTING: {
    value: 'SUPPORTING',
    label: 'Supporting',
    icon: ICONS.TACTICAL.SUPPORTING, // 🛡️
    
    // Casualty multiplier (baseline)
    casualty_multiplier: 1.0,
    
    // Description
    description: 'Supports main effort - fixes enemy forces, provides flank security',
    
    // Tactical implications
    priority: 'MEDIUM',
    resources_priority: 'SECOND',
    commander_focus: 'SECONDARY',
    
    // Medical implications
    medical_priority: 'MEDIUM',
    expected_casualties: 'MODERATE',
    evacuation_priority: 'ROUTINE',
    
    // Examples
    examples: [
      'Brigade fixing enemy while main effort maneuvers',
      'Division providing flank security',
      'Battalion conducting supporting attack'
    ],
    
    // Romanian context
    romanian_term: 'Efort de Sprijin',
    
    // Teaching points
    teaching_points: [
      'Receives resources after main effort',
      'Critical for main effort success',
      'Moderate casualty rates',
      'Medical support adequate but not priority',
      'Standard evacuation procedures'
    ]
  },

  RESERVE: {
    value: 'RESERVE',
    label: 'Reserve',
    icon: ICONS.TACTICAL.RESERVE, // 📦
    
    // Casualty multiplier (minimal - out of contact)
    casualty_multiplier: 0.2,
    
    // Description
    description: 'Out of contact - ready to exploit success or reinforce failure',
    
    // Tactical implications
    priority: 'LOW',
    resources_priority: 'THIRD',
    commander_focus: 'CONTINGENCY',
    
    // Medical implications
    medical_priority: 'LOW',
    expected_casualties: 'MINIMAL',
    evacuation_priority: 'ROUTINE',
    
    // Commitment triggers
    commitment_conditions: [
      'Exploit success of main effort',
      'Reinforce threatened sector',
      'Counter enemy penetration',
      'Assume mission of exhausted unit'
    ],
    
    // Examples
    examples: [
      'Brigade held at division level',
      'Division reserve ready to commit',
      'Battalion prepared to reinforce'
    ],
    
    // Romanian context
    romanian_term: 'Rezervă',
    
    // Teaching points
    teaching_points: [
      'Not in immediate contact',
      'Minimal casualties while in reserve',
      'Casualty rate changes WHEN committed',
      'Medical assets fully operational and rested',
      'Can provide medical support to engaged units',
      'Reserve medical assets can accept overflow'
    ],
    
    // Medical planning notes
    medical_notes: [
      'Reserve MTFs can accept overflow from engaged units',
      'Reserve medical personnel well-rested',
      'Equipment maintained and ready',
      'Blood products available for emergencies',
      'Can deploy forward rapidly if committed'
    ]
  },

  EXPLOITATION: {
    value: 'EXPLOITATION',
    label: 'Exploitation',
    icon: ICONS.TACTICAL.EXPLOITATION, // 🚀
    
    // Casualty multiplier (high mobility, enemy disrupted)
    casualty_multiplier: 1.3,
    
    // Description
    description: 'Exploits enemy weakness - high tempo, deep penetration',
    
    // Tactical implications
    priority: 'HIGH',
    resources_priority: 'IMMEDIATE',
    commander_focus: 'OPPORTUNITY',
    
    // Medical implications
    medical_priority: 'HIGH',
    expected_casualties: 'MODERATE_HIGH',
    evacuation_priority: 'URGENT',
    medical_challenges: [
      'Extended distances from support',
      'Rapid movement complicates evacuation',
      'Medical assets may lag behind combat forces',
      'Prolonged Field Care often required'
    ],
    
    // Characteristics
    characteristics: [
      'High speed of advance',
      'Deep penetration',
      'Bypasses resistance',
      'Disrupts enemy rear areas'
    ],
    
    // Examples
    examples: [
      'Brigade exploiting gap in enemy lines',
      'Division pursuing defeated enemy',
      'Battalion conducting deep raid'
    ],
    
    // Romanian context
    romanian_term: 'Exploatare',
    
    // Teaching points
    teaching_points: [
      'Opportunity-driven mission',
      'High tempo operations',
      'Moderate-high casualties',
      'Medical evacuation challenging due to distance',
      'PFC capabilities critical',
      'Helicopter evacuation essential'
    ],
    
    // Medical planning notes
    medical_notes: [
      'Extended evacuation distances likely',
      'Helicopter evacuation critical',
      'PFC trained personnel essential',
      'Blood products forward with units',
      'Communication with rear medical difficult',
      'May need to establish hasty Role 1 facilities forward'
    ]
  },

  ECONOMY_OF_FORCE: {
    value: 'ECONOMY_OF_FORCE',
    label: 'Economy of Force',
    icon: ICONS.GENERAL.CIRCLE_OUTLINE, // ○
    
    // Casualty multiplier (minimal force, minimal engagement)
    casualty_multiplier: 0.6,
    
    // Description
    description: 'Minimum force on secondary objective - hold or defend with minimal resources',
    
    // Tactical implications
    priority: 'LOW',
    resources_priority: 'MINIMAL',
    commander_focus: 'SECONDARY',
    
    // Medical implications
    medical_priority: 'LOW',
    expected_casualties: 'LOW',
    evacuation_priority: 'ROUTINE',
    
    // Characteristics
    characteristics: [
      'Minimum essential combat power',
      'Secondary or tertiary sectors',
      'Hold or defend missions',
      'Free up forces for main effort'
    ],
    
    // Examples
    examples: [
      'Battalion screening wide sector',
      'Brigade holding quiet sector',
      'Covering force on flank'
    ],
    
    // Romanian context
    romanian_term: 'Economie de Forțe',
    
    // Teaching points
    teaching_points: [
      'Accepts risk in this sector',
      'Frees resources for main effort',
      'Lower casualty rates',
      'Medical support adequate but not extensive',
      'Can provide medical support to other units'
    ]
  },

  URBAN: {
    value: 'URBAN',
    label: 'Urban Operations',
    icon: '🏙️',

    // Casualty multiplier — historically highest in conventional warfare.
    // Stalingrad, Fallujah, Mosul: penetrating wounds +30%, fragmentation+crush combined injury.
    // CREstT urban modifier: 1.65 (range 1.5–1.8 depending on defender density).
    casualty_multiplier: 1.65,

    description: 'Combat in built-up areas — highest casualty rates, complex evacuation, reduced air access',

    priority: 'CRITICAL',
    resources_priority: 'IMMEDIATE',
    commander_focus: 'PRIMARY',

    medical_priority: 'HIGHEST',
    expected_casualties: 'VERY HIGH',
    evacuation_priority: 'URGENT',

    medical_notes: [
      'Fragmentation and blast injury predominate — penetrating + crush pattern',
      'Air MEDEVAC severely degraded by building masonry and AD threat',
      'Ground evacuation routes contested — expect delays',
      'Casualty collection points (CCP) established in hardened structures',
      'Tourniquets and PFC critical — T1 rate elevated',
      'R2B must be positioned close — do NOT rely on long ground evacuation'
    ],

    examples: [
      'Brigade clearing city district',
      'Battalion assaulting defended urban block',
      'Combat in built-up area (FIBUA/MOUT)'
    ],

    romanian_term: 'Acțiuni în Localitate',

    teaching_points: [
      'Highest casualty multiplier in the tool — brief students on why',
      'Air evacuation may be unavailable — plan for ground only',
      'T1:T2:T3 ratio shifts — more T1 than open terrain',
      'Blood consumption per casualty is higher (multiple wounds per hit)'
    ]
  },

  RIVER_CROSSING: {
    value: 'RIVER_CROSSING',
    label: 'River Crossing',
    icon: '🌊',

    // Casualty multiplier — forced river crossings: concentrated, exposed troops,
    // defender has prepared positions covering crossing sites.
    // Historical range: 1.3–1.5. CREstT estimate 1.45 for opposed crossing.
    // Uncontested administrative crossing: use MOVEMENT instead.
    casualty_multiplier: 1.45,

    description: 'Opposed river crossing — crossing force exposed, defender holds far bank',

    priority: 'HIGH',
    resources_priority: 'FIRST',
    commander_focus: 'PRIMARY',

    medical_priority: 'HIGHEST',
    expected_casualties: 'HIGH',
    evacuation_priority: 'URGENT',

    medical_notes: [
      'Drowning and blast injury add to standard wound mix',
      'Crossing window is narrow — mass casualty risk concentrated in hours',
      'Air MEDEVAC restricted to near-bank operations during crossing',
      'Bridgehead must be established before casualty flow can normalise',
      'Near-bank CCP essential — forward surgical team if available',
      'Blood stocks: pre-position on crossing start line'
    ],

    examples: [
      'Battalion assault crossing under fire',
      'Brigade forcing a river obstacle',
      'Division main effort across a defended waterway'
    ],

    romanian_term: 'Forțarea unui Curs de Apă',

    teaching_points: [
      'Casualty rate is time-compressed — most happen in first 2–4 hours',
      'Evacuation chain partially cut until bridge/ferry established',
      'Uncontested crossings use MOVEMENT not RIVER_CROSSING',
      'Compare with MAIN_EFFORT (1.5) — river crossing approaches but does not exceed it'
    ]
  }
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Get mission by value
 * @param {string} value - Mission value (e.g., 'MAIN_EFFORT')
 * @returns {object} Mission configuration
 */
export function getMissionByValue(value) {
  return Object.values(MISSIONS).find(m => m.value === value) || MISSIONS.SUPPORTING;
}

/**
 * Get mission casualty multiplier
 * @param {string} missionValue - Mission value
 * @returns {number} Casualty multiplier
 */
export function getMissionCasualtyMultiplier(missionValue) {
  const mission = getMissionByValue(missionValue);
  return mission.casualty_multiplier;
}

/**
 * Get mission icon
 * @param {string} missionValue - Mission value
 * @returns {string} Icon character
 */
export function getMissionIcon(missionValue) {
  const mission = getMissionByValue(missionValue);
  return mission.icon;
}

/**
 * Get mission label
 * @param {string} missionValue - Mission value
 * @returns {string} Human-readable label
 */
export function getMissionLabel(missionValue) {
  const mission = getMissionByValue(missionValue);
  return mission.label;
}

/**
 * Get missions as array (for dropdowns)
 * @returns {Array} Array of mission objects
 */
export function getMissionsArray() {
  return Object.values(MISSIONS);
}

/**
 * Calculate combined mission multiplier for nested units
 * Example: Division MAIN_EFFORT (1.5x) with Brigade SUPPORTING (1.0x)
 * Result: 1.5 × 1.0 = 1.5x
 * 
 * @param {string} divisionMission - Division level mission
 * @param {string} brigadeMission - Brigade level mission (optional)
 * @param {string} battalionMission - Battalion level mission (optional)
 * @returns {number} Combined casualty multiplier
 */
export function calculateNestedMissionMultiplier(divisionMission, brigadeMission = null, battalionMission = null) {
  let multiplier = 1.0;
  
  // Division level
  if (divisionMission) {
    multiplier *= getMissionCasualtyMultiplier(divisionMission);
  }
  
  // Brigade level (within division)
  if (brigadeMission) {
    multiplier *= getMissionCasualtyMultiplier(brigadeMission);
  }
  
  // Battalion level (within brigade)
  if (battalionMission) {
    multiplier *= getMissionCasualtyMultiplier(battalionMission);
  }
  
  return multiplier;
}

/**
 * Validate mission value
 * @param {string} value - Mission value to validate
 * @returns {boolean} True if valid
 */
export function isValidMission(value) {
  return Object.values(MISSIONS).some(m => m.value === value);
}

// ============================================================================
// OBS-S102-007: OPERATION TYPES — independent second axis for sub-phases
// Role (what function you serve in the echelon) × Operation Type (what you're doing)
// Dominant casualty variable is DEFENDER preparation time, not attacker's.
// ============================================================================

export const OPERATION_TYPES = {
  // ── Offensive ──────────────────────────────────────────────────────────────
  DELIBERATE_ATTACK: {
    value: 'DELIBERATE_ATTACK',
    label: 'Deliberate Attack',
    category: 'OFFENSIVE',
    casualty_multiplier: 1.10,
    description: 'Planned, full preparation, coordinated fires — enemy had time to prepare',
    teaching_point: 'Higher than hasty because the defender also had time to dig in, mine, and pre-register fires.',
    romanian_term: 'Atac Deliberat',
  },
  HASTY_ATTACK: {
    value: 'HASTY_ATTACK',
    label: 'Hasty Attack',
    category: 'OFFENSIVE',
    casualty_multiplier: 1.00,
    description: 'Immediate exploitation of opportunity — enemy not yet consolidated',
    teaching_point: 'Baseline rate: attacker less coordinated, but defender also unprepared. The two effects cancel.',
    romanian_term: 'Atac din Mers',
  },
  URBAN: {
    value: 'URBAN',
    label: 'Urban Operations (MOUT)',
    category: 'OFFENSIVE',
    casualty_multiplier: 1.65,
    description: 'Combat in built-up areas — highest conventional casualty rate (Mariupol, Fallujah, Mosul)',
    teaching_point: 'Air MEDEVAC severely degraded. Fragmentation + blast + crush injury pattern. T1 rate elevated.',
    romanian_term: 'Acțiuni în Localitate',
  },
  RIVER_CROSSING: {
    value: 'RIVER_CROSSING',
    label: 'River Crossing (Opposed)',
    category: 'OFFENSIVE',
    casualty_multiplier: 1.45,
    description: 'Forced crossing — crossing force exposed, defender holds far bank with prepared positions',
    teaching_point: 'Casualty rate time-compressed into crossing window. Bridgehead must be secured before flow normalises.',
    romanian_term: 'Forțarea unui Curs de Apă',
  },
  EXPLOITATION: {
    value: 'EXPLOITATION',
    label: 'Exploitation',
    category: 'OFFENSIVE',
    casualty_multiplier: 1.30,
    description: 'Exploiting enemy weakness — high tempo, deep penetration, dispersed formations',
    teaching_point: 'Medical evacuation challenging: extended distances, medical assets may lag. PFC critical.',
    romanian_term: 'Exploatare',
  },
  PURSUIT: {
    value: 'PURSUIT',
    label: 'Pursuit',
    category: 'OFFENSIVE',
    casualty_multiplier: 1.15,
    description: 'Enemy fleeing but still capable of contact — flanks exposed, extended supply lines',
    teaching_point: 'Lower than exploitation: enemy disorder is greater, but friendly overextension risk.',
    romanian_term: 'Urmărire',
  },
  AIRBORNE_ASSAULT: {
    value: 'AIRBORNE_ASSAULT',
    label: 'Airborne / Air Assault',
    category: 'OFFENSIVE',
    casualty_multiplier: 1.35,
    description: 'Insertion under fire — limited organic support, no guaranteed ground link',
    teaching_point: 'Until linkup: no ground evacuation. Air MEDEVAC is the only option. PFC window is extended.',
    romanian_term: 'Asalt Aerian / Paraşutare',
  },
  // ── Defensive ──────────────────────────────────────────────────────────────
  DEFEND: {
    value: 'DEFEND',
    label: 'Defend (Prepared)',
    category: 'DEFENSIVE',
    casualty_multiplier: 0.75,
    description: 'Prepared defensive positions — attacker at disadvantage (mines, obstacles, pre-registered fires)',
    teaching_point: 'Defender advantage: 0.75× baseline. MTFs can be positioned; evacuation routes pre-planned.',
    romanian_term: 'Apărare Pregătită',
  },
  HASTY_DEFENSE: {
    value: 'HASTY_DEFENSE',
    label: 'Hasty Defense',
    category: 'DEFENSIVE',
    casualty_multiplier: 0.90,
    description: 'Less preparation time — positions partially dug, obstacles incomplete',
    teaching_point: 'Still advantaged vs. attacker, but less than prepared defense. Closer to baseline.',
    romanian_term: 'Apărare Grăbită',
  },
  DELAY: {
    value: 'DELAY',
    label: 'Delay',
    category: 'DEFENSIVE',
    casualty_multiplier: 0.85,
    description: 'Trading space for time — continued contact while conducting retrograde',
    teaching_point: 'Units exposed during bound-back transitions. Evacuation direction reverses: plan accordingly.',
    romanian_term: 'Întârziere',
  },
  WITHDRAWAL: {
    value: 'WITHDRAWAL',
    label: 'Withdrawal (Organized)',
    category: 'DEFENSIVE',
    casualty_multiplier: 0.80,
    description: 'Organized retrograde under enemy pressure — rearguard action',
    teaching_point: 'Casualties concentrated on rearguard. Evacuation chain must move with the withdrawal axis.',
    romanian_term: 'Retragere',
  },
  // ── Other ──────────────────────────────────────────────────────────────────
  ADMINISTRATIVE_MARCH: {
    value: 'ADMINISTRATIVE_MARCH',
    label: 'Administrative March',
    category: 'OTHER',
    casualty_multiplier: 0.05,
    description: 'Admin column movement — no contact expected. Casualties are DNBI and accidents only.',
    teaching_point: 'Near-zero battle casualties. DNBI (heat, dehydration, accidents) dominate. Plan hydration, rest halts, vehicle spacing.',
    romanian_term: 'Marș Administrativ',
  },
  TACTICAL_MARCH: {
    value: 'TACTICAL_MARCH',
    label: 'Tactical March',
    category: 'OTHER',
    casualty_multiplier: 0.25,
    description: 'Tactical formation movement — contact possible. Advance guard out, weapons ready.',
    teaching_point: 'Low battle casualties (ambush, drone, air threat) on top of DNBI. Movement under enemy pressure → use DELAY or WITHDRAWAL instead.',
    romanian_term: 'Marș Tactic',
  },
};

/**
 * OBS-S102-007: Get operation type casualty multiplier.
 * Returns 1.0 if operation_type is null/undefined (no additional modifier).
 */
export function getOperationTypeCasualtyMultiplier(operationType) {
  if (!operationType) return 1.0;
  return OPERATION_TYPES[operationType]?.casualty_multiplier ?? 1.0;
}

/**
 * OBS-S102-007: Combined sub-phase multiplier = role × operation_type, capped at 2.50.
 * Worst realistic case: MAIN_EFFORT (1.5) × URBAN (1.65) = 2.475.
 * Falls back to getMissionCasualtyMultiplier(missionType) when role is undefined (old data).
 */
export function getSubPhaseCombinedMultiplier(role, operationType) {
  const roleMult   = getMissionCasualtyMultiplier(role || 'SUPPORTING');
  const opTypeMult = getOperationTypeCasualtyMultiplier(operationType);
  return Math.min(roleMult * opTypeMult, 2.50);
}

/**
 * OBS-S102-007: Migrate old single-field sub-phase to new role + operation_type shape.
 * Safe to call on already-migrated sub-phases (no-op when role field present).
 */
export function migrateSubPhase(sp) {
  if (sp.role !== undefined) return sp; // already new format
  const old = sp.mission_type || 'SUPPORTING';
  // Values that were operation types masquerading as roles in the old model
  const OP_TYPE_ONLY = ['URBAN', 'RIVER_CROSSING'];
  // MOVEMENT → TACTICAL_MARCH (renamed in OBS-S102-007 revision)
  const opType = old === 'MOVEMENT'      ? 'TACTICAL_MARCH'
               : OP_TYPE_ONLY.includes(old) ? old
               : null;
  return {
    ...sp,
    role:           (OP_TYPE_ONLY.includes(old) || old === 'MOVEMENT') ? 'SUPPORTING' : old,
    operation_type: opType,
    // mission_type preserved — calculator BC fallback reads it when role absent
  };
}

/**
 * OBS-S102-007: Get operation types as grouped array for dropdowns.
 */
export function getOperationTypesArray() {
  return Object.values(OPERATION_TYPES);
}

// ============================================================================
// TEACHING SCENARIOS
// ============================================================================

export const MISSION_SCENARIOS = {
  RIVER_CROSSING: {
    scenario: 'Division River Crossing Operation',
    division_mission: 'MAIN_EFFORT',
    brigades: [
      {
        name: 'Brigade 1 (LEFT)',
        mission: 'MAIN_EFFORT',
        description: 'Crosses river first, establishes bridgehead',
        casualties_relative: 'HIGHEST (1.5 × 1.5 = 2.25x)'
      },
      {
        name: 'Brigade 2 (CENTER)',
        mission: 'SUPPORTING',
        description: 'Crosses second, expands bridgehead',
        casualties_relative: 'HIGH (1.5 × 1.0 = 1.5x)'
      },
      {
        name: 'Brigade 3 (RIGHT)',
        mission: 'RESERVE',
        description: 'Remains on near side, ready to exploit',
        casualties_relative: 'MINIMAL (1.5 × 0.2 = 0.3x)'
      }
    ],
    teaching_point: 'Mission assignment dramatically affects casualties even though all brigades are in same division operation'
  },

  DELIBERATE_ATTACK: {
    scenario: 'Corps Deliberate Attack',
    corps_mission: 'MAIN_EFFORT',
    divisions: [
      {
        name: 'Division 1 (CENTER)',
        mission: 'MAIN_EFFORT',
        description: 'Breaches enemy main line',
        casualties_relative: 'HIGHEST (1.5x)'
      },
      {
        name: 'Division 2 (LEFT)',
        mission: 'SUPPORTING',
        description: 'Fixes enemy forces',
        casualties_relative: 'MODERATE (1.0x)'
      },
      {
        name: 'Division 3 (RIGHT)',
        mission: 'ECONOMY_OF_FORCE',
        description: 'Screens flank',
        casualties_relative: 'LOW (0.6x)'
      }
    ],
    teaching_point: 'Different missions allow concentration of combat power while accepting risk elsewhere'
  }
};

// ============================================================================
// DEFAULT EXPORT
// ============================================================================

export default MISSIONS;
